import {
    UsercryptCheckinEntry,
    UsercryptCheckinEntrySummary,
} from '@calm/evexia-web-sdk';
import resultsMessages from '@rhg/lang/messages/results';
import { selfHarmScoreId } from '@rhg/utils/constants';
import { CheckinTypeKey, classifyResult } from '@rhg/utils/usercrypt/checkins';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    CheckinResult,
    CheckinResultItem,
} from '../use-screening-results/types';
import { useStandaloneResultsValues } from '../use-standalone-results-values';

export function useStandaloneScreeningResults() {
    const { formatMessage } = useIntl();
    const { scores } = useStandaloneResultsValues();

    const gadLatestEntry = useMemo(
        () =>
            scores
                ? ({
                      key: 'gad',
                      latestEntry: {
                          checkins: {
                              values: [{ id: 'gad_score', value: scores.gad }],
                          },
                      },
                  } as unknown as UsercryptCheckinEntrySummary)
                : undefined,
        [scores]
    );
    const phqLatestEntry = useMemo(
        () =>
            scores
                ? ({
                      key: 'phq',
                      latestEntry: {
                          checkins: {
                              values: [{ id: 'phq_score', value: scores.phq }],
                          },
                      },
                  } as unknown as UsercryptCheckinEntrySummary)
                : undefined,
        [scores]
    );
    const selfHarmLatestEntry = useMemo(
        () =>
            scores
                ? ({
                      key: 'self_harm_thoughts',
                      latestEntry: {
                          checkins: {
                              values: [
                                  {
                                      id: selfHarmScoreId,
                                      value: scores.selfHarmThoughts,
                                  },
                              ],
                          },
                      },
                  } as unknown as UsercryptCheckinEntrySummary)
                : undefined,
        [scores]
    );
    const createEntry = useCallback(
        (
            entryType: CheckinTypeKey,
            entry: UsercryptCheckinEntry
        ): CheckinResultItem => {
            const classification =
                entryType === CheckinTypeKey.SelfHarm
                    ? classifyResult({ selfHarmEntry: entry })
                    : entryType === CheckinTypeKey.Gad
                    ? classifyResult({ gadEntry: entry })
                    : classifyResult({
                          phqEntry: entry,
                          useModerateSevere: true,
                      });
            return {
                entry,
                score: entry.checkins.values[0].value ?? 0,
                classification,
                label: formatMessage(
                    resultsMessages[`results.label.${classification}`]
                ),
            };
        },
        [formatMessage]
    );

    const createResult = useCallback(
        (
            gadEntry: UsercryptCheckinEntry,
            phqEntry: UsercryptCheckinEntry,
            selfHarmEntry?: UsercryptCheckinEntry
        ): CheckinResult => {
            const resultClassification = classifyResult({
                gadEntry,
                phqEntry,
                selfHarmEntry,
            });
            return {
                gad: createEntry(CheckinTypeKey.Gad, gadEntry),
                phq: createEntry(CheckinTypeKey.Phq, phqEntry),
                ...(selfHarmEntry
                    ? {
                          selfHarm: createEntry(
                              CheckinTypeKey.SelfHarm,
                              selfHarmEntry
                          ),
                      }
                    : {}),
                resultClassification,
                resultClassificationLabel: formatMessage(
                    resultsMessages[`results.label.${resultClassification}`]
                ),
            };
        },
        [createEntry, formatMessage]
    );

    const latestResult = useMemo(
        () =>
            gadLatestEntry?.latestEntry && phqLatestEntry?.latestEntry
                ? createResult(
                      gadLatestEntry?.latestEntry,
                      phqLatestEntry?.latestEntry,
                      selfHarmLatestEntry?.latestEntry
                  )
                : undefined,
        [gadLatestEntry, phqLatestEntry, createResult, selfHarmLatestEntry]
    );

    return {
        latestResult,
        hasMultipleResults: false,
        isFetched: !!latestResult,
        createEntry,
        createResult,
        gadCheckins: latestResult ? [latestResult.gad.entry] : [],
        phqCheckins: latestResult ? [latestResult.phq.entry] : [],
    };
}
